import React from 'react'
import { Button, Divider, useTheme } from '@material-ui/core'
import { Container } from '../../components'
import { formatScheduleDateInZone, resolveRoute, Routes } from '../../helpers'
import { EndOfTurnReport, Schedule } from '../../models'
import { useHistory } from 'react-router'
import { typographyStyle } from '../../theme/typographyStyle'
import { useTimezone } from '../../contexts/timezone'

interface Props {
    scheduleDetail?: Schedule
    endOfTurnReportList: EndOfTurnReport[]
    id?: string
    showRateVendors?: boolean
    onClickRateVendors?: () => void
}

const HEADER_HEIGHT = 100

export const ArchivedScheduleHeader = (props: Props) => {
    const {
        scheduleDetail,
        endOfTurnReportList,
        id,
        showRateVendors,
        onClickRateVendors,
    } = props

    const theme = useTheme()
    const history = useHistory()
    const { currentTimezone, propertyTimezone } = useTimezone()
    const displayZone = propertyTimezone ?? currentTimezone

    const startDateLabel = formatScheduleDateInZone(
        scheduleDetail?.start_date,
        displayZone,
    )
    const endDateLabel = formatScheduleDateInZone(
        scheduleDetail?.end_date,
        displayZone,
    )

    let goBacks = 0
    let doubleGoBacks = 0
    let goBackMax = 0
    let assignedCount = 0
    let completeCount = 0
    let approvedCount = 0

    endOfTurnReportList.forEach((currentEndOfTurnReport) => {
        currentEndOfTurnReport.unit_config_data.forEach((ucData) => {
            goBacks += ucData.go_back_count
            goBackMax += ucData.go_back_max
            doubleGoBacks += ucData.double_go_back
            assignedCount += ucData.assigned_count
            completeCount += ucData.complete_count
            approvedCount += ucData.approved_count
        })
    })

    const textTheme: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        marginRight: theme.spacing(1),
        fontWeight: 300,
    }
    const numberTheme: React.CSSProperties = {
        ...textTheme,
        fontWeight: 700,
    }
    return (
        <Container style={{ flexDirection: 'column' }}>
            <Container
                style={{
                    minHeight: HEADER_HEIGHT,
                    maxHeight: HEADER_HEIGHT,
                    justifyContent: 'center',
                    padding: theme.spacing(1, 2),
                }}
            >
                <Container style={{ flexDirection: 'column', flex: 1 }}>
                    <Container>
                        <Container
                            style={{
                                flexDirection: 'column',
                                marginRight: theme.spacing(2),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                }}
                            >
                                {scheduleDetail?.name}
                            </span>

                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle2,
                                    ),
                                }}
                            >
                                {scheduleDetail?.schedule_id}
                            </span>
                        </Container>

                        <Container
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                flexDirection: 'column',
                            }}
                        >
                            <span style={textTheme}>{startDateLabel}</span>
                            <span style={textTheme}>{endDateLabel}</span>
                        </Container>
                        <Container
                            style={{ marginLeft: theme.spacing(3), flex: 1 }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                    marginRight: theme.spacing(2),
                                }}
                            >
                                Turn Statistics:
                            </span>
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    marginTop: theme.spacing(0.5),
                                }}
                            >
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>
                                        Assigned Count:{' '}
                                    </span>
                                    <span style={numberTheme}>
                                        {assignedCount}
                                    </span>
                                </Container>
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>
                                        Complete Count:{' '}
                                    </span>
                                    <span style={numberTheme}>
                                        {completeCount}
                                    </span>
                                </Container>
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>
                                        Approved Count:{' '}
                                    </span>
                                    <span style={numberTheme}>
                                        {approvedCount}
                                    </span>
                                </Container>
                            </Container>
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    marginTop: theme.spacing(0.5),
                                }}
                            >
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>Go Backs: </span>
                                    <span style={numberTheme}>{goBacks}</span>
                                </Container>
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>
                                        Double Go Backs:{' '}
                                    </span>
                                    <span style={numberTheme}>
                                        {doubleGoBacks}
                                    </span>
                                </Container>
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>3+ Go Backs: </span>
                                    <span style={numberTheme}>{goBackMax}</span>
                                </Container>
                            </Container>
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    marginTop: theme.spacing(0.5),
                                }}
                            >
                                <Container
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    <span style={textTheme}>
                                        Approved First Time:{' '}
                                    </span>
                                    <span style={numberTheme}>
                                        {approvedCount - goBacks}
                                    </span>
                                </Container>
                            </Container>
                        </Container>
                    </Container>
                </Container>
            </Container>
            <Container
                style={{
                    padding: theme.spacing(1, 2),
                    gap: theme.spacing(1),
                }}
            >
                {showRateVendors && (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onClickRateVendors}
                    >
                        Rate vendors
                    </Button>
                )}
                <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                        if (id) {
                            const route = Routes.apartmentScheduleDetail
                            history.push(resolveRoute(route, ':id', id))
                        }
                    }}
                >
                    View Schedule
                </Button>
            </Container>
            <Divider />
        </Container>
    )
}
